import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * Timezone the salons operate in. Business hours stored on a tenant
 * (e.g. "09:00") are wall-clock times in this zone, so all times shown
 * to users must be rendered in it regardless of the viewer's browser.
 */
export const SALON_TIMEZONE = 'Asia/Beirut';

/**
 * Convert an instant to a Date whose *local* fields (getHours, getDate, …)
 * read as the wall-clock time in {@link SALON_TIMEZONE}.
 *
 * Pass server-provided instants (ISO strings, `appointment.startTime`,
 * available slots) through this before handing them to date-fns helpers
 * like `isSameDay` or calendar positioning math. For plain formatting,
 * prefer {@link formatSalon}.
 */
export function toSalonTime(input: Date | string): Date {
  return toZonedTime(input, SALON_TIMEZONE);
}

/**
 * Inverse of {@link toSalonTime}: interpret a wall-clock value as a time in
 * {@link SALON_TIMEZONE} and return the matching absolute instant.
 *
 * Use this for `<input type="datetime-local">` values, which the browser
 * produces/consumes in its own timezone. Accepts a "yyyy-MM-ddTHH:mm" string
 * or a Date whose local fields hold the intended salon wall-clock.
 */
export function fromSalonWallClock(input: string | Date): Date {
  return fromZonedTime(input, SALON_TIMEZONE);
}

/** Format an instant with a date-fns pattern, rendered in the salon timezone. */
export function formatSalon(input: Date | string, pattern: string): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  return formatInTimeZone(date, SALON_TIMEZONE, pattern);
}

/** "Now", expressed as salon wall-clock — use as the today/current reference. */
export function salonNow(): Date {
  return toZonedTime(new Date(), SALON_TIMEZONE);
}
